import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Loader, AlertCircle } from 'lucide-react';
import {
  fetchBookingByOrderId,
  fetchBookingById,
  fetchBookingItems,
  fetchInventoryItemsForBooking,
  fetchMerchant,
  fetchMerchantConfig,
  fetchLocation,
  submitCompleteReturn,
  callWebhook
} from './utils/supabaseClickShare';
import {
  getDefaultConfiguration,
  buildReturnFlow,
  getCompletedSections
} from './utils/configHelpers';
import ConfigValidator from './utils/configValidator';
import { injectMerchantColors, getMerchantButtonClasses, getMerchantSecondaryColor } from './utils/merchantColors';
import DynamicPhotoUpload from './components/DynamicPhotoUpload';
import DynamicQuestionSet from './components/DynamicQuestionSet';
import ItemLevelFlow from './components/ItemLevelFlow';
import DynamicThankYou from './components/DynamicThankYou';

/**
 * Main App Component for Click&Share v2 Return App
 *
 * Flow:
 * 1. Parse URL parameters (order_id, lang)
 * 2. Load booking data from Click&Share v2
 * 3. Load merchant configuration
 * 4. Build dynamic flow based on configuration
 * 5. Guide user through sections
 * 6. Submit complete return
 */
const App = () => {
  // Loading states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // URL parameters
  const [orderId, setOrderId] = useState('');
  const [language, setLanguage] = useState('de');

  // Data from database
  const [booking, setBooking] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [merchantConfig, setMerchantConfig] = useState(null);
  const [location, setLocation] = useState(null);

  // Flow state
  const [flow, setFlow] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const navigatingBackRef = useRef(false);

  // Collected data
  const [bookingPhotos, setBookingPhotos] = useState([]);
  const [bookingLevelAnswers, setBookingLevelAnswers] = useState({});
  const [itemReturns, setItemReturns] = useState([]);
  const [itemPhotos, setItemPhotos] = useState({});

  // Submission state
  const [submissionComplete, setSubmissionComplete] = useState(false);

  /**
   * Initialize app on mount
   */
  useEffect(() => {
    console.log('[App] Component mounted/remounted, initializing app');
    initializeApp();
  }, []);

  // Track section changes
  useEffect(() => {
    console.log('[App] Current section index changed to:', currentSectionIndex, 'Section:', flow[currentSectionIndex]?.type);
  }, [currentSectionIndex, flow]);

  // Detect page reloads
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      console.warn('[App] ⚠️ PAGE IS RELOADING/UNLOADING!');
    };

    const handleError = (event) => {
      console.error('[App] ❌ GLOBAL ERROR:', event.error || event.message);
      console.error('[App] Error stack:', event.error?.stack);
    };

    const handleUnhandledRejection = (event) => {
      console.error('[App] ❌ UNHANDLED PROMISE REJECTION:', event.reason);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const initializeApp = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Parse URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const bookingIdParam = urlParams.get('id');
      const orderIdParam = urlParams.get('order_id'); // DEPRECATED
      const merchantIdParam = urlParams.get('merchant_id');
      const locationIdParam = urlParams.get('location_id');
      const langParam = urlParams.get('lang') || 'de';

      setLanguage(langParam);

      let bookingData;
      let merchantData;
      let locationData;
      let merchantConfig;
      let allItems;

      // 2. Optimized parallel fetching when all IDs are provided
      if (bookingIdParam && merchantIdParam && locationIdParam) {
        console.log('Initializing app with direct IDs (fully optimized):', { bookingIdParam, merchantIdParam, locationIdParam });

        // Fetch booking, merchant, config, location, AND booking items in parallel
        const [booking, merchant, config, location, items] = await Promise.all([
          fetchBookingById(bookingIdParam),
          fetchMerchant(merchantIdParam),
          fetchMerchantConfig(merchantIdParam).catch(() => null),
          fetchLocation(locationIdParam),
          fetchBookingItems(bookingIdParam)
        ]);

        bookingData = booking;
        merchantData = merchant;
        locationData = location;
        merchantConfig = config;
        allItems = items;

        if (!bookingData) {
          throw new Error(`Booking not found: ${bookingIdParam}`);
        }

        setBooking(bookingData);
        setMerchant(merchantData);
        setLocation(locationData);

        // Inject merchant colors into CSS
        injectMerchantColors(merchantData);

        console.log('Parallel fetch complete (5 queries) - Booking loaded:', bookingData.id);

      } else if (bookingIdParam) {
        // 3. Fetch by booking ID with booking items in parallel (semi-optimized)
        console.log('Initializing app with booking ID (semi-optimized):', bookingIdParam);

        // Fetch booking and booking items in parallel first
        const [booking, items] = await Promise.all([
          fetchBookingById(bookingIdParam),
          fetchBookingItems(bookingIdParam)
        ]);

        bookingData = booking;
        allItems = items;

        if (!bookingData) {
          throw new Error(`Booking not found: ${bookingIdParam}`);
        }
        setBooking(bookingData);

        console.log('Booking loaded:', bookingData.id);

        // Fetch merchant, config, and location in parallel
        const [merchant, config, location] = await Promise.all([
          fetchMerchant(bookingData.merchant_id),
          fetchMerchantConfig(bookingData.merchant_id).catch(() => null),
          fetchLocation(bookingData.location_id)
        ]);

        merchantData = merchant;
        locationData = location;
        merchantConfig = config;

        setMerchant(merchantData);
        setLocation(locationData);

        // Inject merchant colors into CSS
        injectMerchantColors(merchantData);

      } else if (orderIdParam) {
        // 4. DEPRECATED: Fetch by order_id (kept for backward compatibility)
        console.warn('[DEPRECATED] Using order_id parameter. Please use id parameter instead for better performance.');

        setOrderId(orderIdParam);

        // Fetch booking by order_id
        bookingData = await fetchBookingByOrderId(orderIdParam);
        if (!bookingData) {
          throw new Error(`Booking not found: ${orderIdParam}`);
        }
        setBooking(bookingData);

        console.log('Booking loaded:', bookingData.id);

        // Fetch merchant, config, location, and booking items in parallel
        const [merchant, config, location, items] = await Promise.all([
          fetchMerchant(bookingData.merchant_id),
          fetchMerchantConfig(bookingData.merchant_id).catch(() => null),
          fetchLocation(bookingData.location_id),
          fetchBookingItems(bookingData.id)
        ]);

        merchantData = merchant;
        locationData = location;
        merchantConfig = config;
        allItems = items;

        setMerchant(merchantData);
        setLocation(locationData);

        // Inject merchant colors into CSS
        injectMerchantColors(merchantData);

      } else {
        throw new Error('Missing required parameter: id');
      }

      // Set Sentry context for better error reporting
      Sentry.setContext('booking', {
        bookingId: bookingData?.id,
        merchantId: merchantData?.id,
        merchantName: merchantData?.business_name,
      });

      // 4. Validate and set merchant config
      if (!merchantConfig) {
        console.warn('No merchant config found, using defaults');
        merchantConfig = getDefaultConfiguration();
      }

      const validationErrors = ConfigValidator.validateFlowConfig(merchantConfig);
      if (validationErrors.length > 0) {
        console.error('Configuration validation errors:', validationErrors);
        merchantConfig = getDefaultConfiguration();
      }

      setMerchantConfig(merchantConfig);

      // 5. Filter booking items (remove addons)
      const items = allItems.filter(item => item.product_type !== 'addon');
      setBookingItems(items);

      console.log(`Filtered ${items.length} items from ${allItems.length} total (excluded ${allItems.length - items.length} addons)`);

      // 6. Fetch inventory items (if any)
      if (items.length > 0) {
        const inventory = await fetchInventoryItemsForBooking(items);
        setInventoryItems(inventory);
      }

      // 7. Build dynamic flow
      const dynamicFlow = buildReturnFlow(merchantConfig, bookingData, items);
      setFlow(dynamicFlow);

      console.log('Flow built with sections:', dynamicFlow.map(s => s.id));

      setLoading(false);
    } catch (err) {
      console.error('Error initializing app:', err);
      Sentry.captureException(err, {
        tags: { phase: 'initialization' },
        extra: {
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
      });
      setError(err.message || 'Failed to load booking data');
      setLoading(false);
    }
  };

  /**
   * Handle photo section complete
   */
  const handlePhotosComplete = (photos) => {
    if (navigatingBackRef.current) {
      console.log('[App] Ignoring handlePhotosComplete during back navigation');
      return;
    }
    console.log('Photos complete:', photos.length);
    setBookingPhotos(photos);
    moveToNextSection({ bookingPhotos: photos });
  };

  /**
   * Handle booking-level questions complete
   */
  const handleBookingQuestionsComplete = (answers) => {
    if (navigatingBackRef.current) {
      console.log('[App] Ignoring handleBookingQuestionsComplete during back navigation');
      return;
    }
    console.log('Booking questions complete');
    setBookingLevelAnswers(answers);
    moveToNextSection({ bookingLevelAnswers: answers });
  };

  /**
   * Handle item-level questions complete
   */
  const handleItemQuestionsComplete = ({ itemReturns: returns, itemPhotos: photos }) => {
    if (navigatingBackRef.current) {
      console.log('[App] Ignoring handleItemQuestionsComplete during back navigation');
      return;
    }
    console.log('Item questions complete:', returns.length);
    setItemReturns(returns);
    setItemPhotos(photos);
    moveToNextSection({ itemReturns: returns, itemPhotos: photos });
  };

  /**
   * Move to next section in flow
   * @param {Object} freshData - Optional fresh data to pass to submit (overrides state)
   */
  const moveToNextSection = (freshData = {}) => {
    // Only proceed if not navigating back (prevents auto-complete loop)
    if (navigatingBackRef.current) {
      console.log('[App] Ignoring moveToNextSection during back navigation');
      return;
    }

    const nextIndex = currentSectionIndex + 1;

    if (nextIndex >= flow.length) {
      // All sections complete, submit
      handleSubmit(freshData);
    } else {
      const nextSection = flow[nextIndex];

      // If next section is completion, submit first
      if (nextSection.type === 'completion') {
        handleSubmit(freshData);
      } else {
        setCurrentSectionIndex(nextIndex);
      }
    }
  };

  /**
   * Move to previous section in flow
   */
  const moveToPreviousSection = () => {
    if (currentSectionIndex > 0) {
      navigatingBackRef.current = true;

      // Find the previous non-empty section
      let targetIndex = currentSectionIndex - 1;

      // Skip sections with no questions (booking question sets with empty questions array)
      while (targetIndex >= 0) {
        const targetSection = flow[targetIndex];

        // Check if this is a question_set with no questions
        if (targetSection.type === 'question_set' &&
            targetSection.config?.scope === 'booking' &&
            (!targetSection.config?.questions || targetSection.config.questions.length === 0)) {
          // Skip this section, go to previous one
          targetIndex--;
        } else {
          // Found a non-empty section
          break;
        }
      }

      // If we found a valid section, navigate to it
      if (targetIndex >= 0) {
        setCurrentSectionIndex(targetIndex);
      }

      // Clear the flag after a short delay to allow normal navigation to resume
      setTimeout(() => {
        navigatingBackRef.current = false;
      }, 100);
    }
  };

  /**
   * Handle complete return submission
   * @param {Object} freshData - Optional fresh data to use (overrides state)
   */
  const handleSubmit = async (freshData = {}) => {
    try {
      setSubmitting(true);
      setError(null);

      // Use fresh data if provided, otherwise fall back to state
      const dataToSubmit = {
        bookingPhotos: freshData.bookingPhotos ?? bookingPhotos,
        bookingLevelAnswers: freshData.bookingLevelAnswers ?? bookingLevelAnswers,
        itemReturns: freshData.itemReturns ?? itemReturns,
        itemPhotos: freshData.itemPhotos ?? itemPhotos
      };

      console.log('Submitting return...');
      console.log('Item returns being submitted:', dataToSubmit.itemReturns);
      console.log('Item photos being submitted:', dataToSubmit.itemPhotos);
      console.log('Booking level answers:', dataToSubmit.bookingLevelAnswers);

      const completedSections = getCompletedSections(
        flow,
        dataToSubmit.bookingLevelAnswers,
        dataToSubmit.itemReturns,
        dataToSubmit.bookingPhotos.length > 0
      );

      const result = await submitCompleteReturn({
        booking,
        merchantConfig,
        completedSections,
        bookingLevelAnswers: dataToSubmit.bookingLevelAnswers,
        itemReturns: dataToSubmit.itemReturns,
        bookingPhotos: dataToSubmit.bookingPhotos,
        itemPhotos: dataToSubmit.itemPhotos
      });

      console.log('Return submitted successfully:', result.bookingReturn.id);

      // Optionally call webhook (don't block on failure)
      try {
        await callWebhook(booking.order_id, location?.location_name, 'completed');
      } catch (webhookError) {
        console.warn('Webhook call failed (non-critical):', webhookError);
      }

      setSubmissionComplete(true);
      setSubmitting(false);

      // Move to thank you section
      const thankYouIndex = flow.findIndex(s => s.type === 'completion');
      if (thankYouIndex !== -1) {
        setCurrentSectionIndex(thankYouIndex);
      }

    } catch (err) {
      console.error('Error submitting return:', err);
      Sentry.captureException(err, {
        tags: { phase: 'submission' },
        extra: { bookingId: booking?.id },
      });
      setError(err.message || (language === 'de'
        ? 'Fehler beim Einreichen der Rückgabe'
        : 'Error submitting return'
      ));
      setSubmitting(false);
    }
  };

  /**
   * Loading screen
   */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-pink-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">
            {language === 'de' ? 'Lade Buchung...' : 'Loading booking...'}
          </p>
        </div>
      </div>
    );
  }

  /**
   * Error screen
   */
  if (error && !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">
            {language === 'de' ? 'Fehler' : 'Error'}
          </h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className={`w-full px-6 py-3 rounded-lg font-semibold ${
              merchant
                ? getMerchantButtonClasses(merchant)
                : 'bg-rose-500 text-white hover:bg-rose-600 transition-colors'
            }`}
          >
            {language === 'de' ? 'Erneut versuchen' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  /**
   * Submitting screen
   */
  if (submitting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader
            className="w-12 h-12 animate-spin mx-auto mb-4"
            style={{ color: merchant?.color2 || '#ec4899' }}
          />
          <p className="text-gray-600 text-lg">
            {language === 'de' ? 'Rückgabe wird eingereicht...' : 'Submitting return...'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {language === 'de' ? 'Bitte warten...' : 'Please wait...'}
          </p>
        </div>
      </div>
    );
  }

  /**
   * Render current section
   */
  if (flow.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {language === 'de' ? 'Keine Konfiguration' : 'No Configuration'}
          </h2>
          <p className="text-gray-600">
            {language === 'de'
              ? 'Für diesen Händler ist keine Rückgabekonfiguration vorhanden.'
              : 'No return configuration found for this merchant.'}
          </p>
        </div>
      </div>
    );
  }

  const currentSection = flow[currentSectionIndex];

  // Render section based on type
  switch (currentSection.type) {
    case 'photo_upload':
      return (
        <DynamicPhotoUpload
          config={currentSection.config}
          language={language}
          merchant={merchant}
          initialPhotos={bookingPhotos}
          onPhotosChange={setBookingPhotos}
          onComplete={handlePhotosComplete}
          onBack={currentSectionIndex > 0 ? moveToPreviousSection : null}
        />
      );

    case 'question_set':
      if (currentSection.config.scope === 'booking') {
        return (
          <DynamicQuestionSet
            config={currentSection.config}
            language={language}
            merchant={merchant}
            onComplete={handleBookingQuestionsComplete}
            onBack={currentSectionIndex > 0 ? moveToPreviousSection : null}
          />
        );
      } else if (currentSection.config.scope === 'item') {
        return (
          <ItemLevelFlow
            config={currentSection.config}
            language={language}
            merchant={merchant}
            bookingItems={bookingItems}
            inventoryItems={inventoryItems}
            onComplete={handleItemQuestionsComplete}
            onBack={moveToPreviousSection}
          />
        );
      }
      break;

    case 'completion':
      return (
        <DynamicThankYou
          config={currentSection.config}
          language={language}
          merchant={merchant}
          googleMapsUrl={location?.google_maps_url}
        />
      );

    default:
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-600">Unknown section type: {currentSection.type}</p>
        </div>
      );
  }
};

export default App;
